// pages/rental/DocumentVerification.jsx - FULLY MOBILE RESPONSIVE

import React, { useState, useEffect, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, showNotification, NOTIFICATION_TYPES } from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../utils/constants';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentIcon,
  CloudArrowUpIcon,
  XCircleIcon,
  EyeIcon,
  TrashIcon,
  ClockIcon,
  CalendarIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  ArrowPathIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const DocumentVerification = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState({
    adhaarFront: false,
    adhaarBack: false,
    drivingLicense: false
  });
  
  const [newDocuments, setNewDocuments] = useState({
    adhaarFront: null,
    adhaarBack: null,
    drivingLicense: null
  });
  
  const [newPreviews, setNewPreviews] = useState({
    adhaarFront: null,
    adhaarBack: null,
    drivingLicense: null
  });
  
  const [existingPreviews, setExistingPreviews] = useState({
    adhaarFront: null,
    adhaarBack: null,
    drivingLicense: null
  });
  
  const [documentStatuses, setDocumentStatuses] = useState({
    adhaarFront: 'PENDING',
    adhaarBack: 'PENDING',
    drivingLicense: 'PENDING'
  });
  
  const [errors, setErrors] = useState({});
  const [timestamps, setTimestamps] = useState({
    createdAt: null,
    updatedAt: null
  });
  
  const [imageErrors, setImageErrors] = useState({
    adhaarFront: false,
    adhaarBack: false,
    drivingLicense: false
  });

  const fileInputRefs = {
    adhaarFront: useRef(null),
    adhaarBack: useRef(null),
    drivingLicense: useRef(null)
  };

  useEffect(() => {
    loadExistingDocuments();
  }, []);

  const getUserId = () => user?.data?.id || user?.id || user?.userId;

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Not available';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getTimeDifference = (timestamp) => {
    if (!timestamp) return '';
    try {
      const now = new Date();
      const past = new Date(timestamp);
      const diffMs = now - past;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return formatDateTime(timestamp);
    } catch (error) {
      return '';
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath;
    return `${API_ENDPOINTS.BASE_URL}/${imagePath}`;
  };

  const loadExistingDocuments = async () => {
    try {
      setLoading(true);
      const userId = getUserId();
      if (!userId) return;

      const response = await api.get(`/api/documents/userdocuments/${userId}`);

      if (response.success && response.data) {
        const docs = response.data;
        
        setExistingPreviews({
          adhaarFront: docs.adhaarFrontImageUrl ? getImageUrl(docs.adhaarFrontImageUrl) : null,
          adhaarBack: docs.adhaarBackImageUrl ? getImageUrl(docs.adhaarBackImageUrl) : null,
          drivingLicense: docs.drivingLicenseImageUrl ? getImageUrl(docs.drivingLicenseImageUrl) : null
        });
        
        setDocumentStatuses({
          adhaarFront: docs.adhaarFrontStatus || 'PENDING',
          adhaarBack: docs.adhaarBackStatus || 'PENDING',
          drivingLicense: docs.licenseStatus || 'PENDING'
        });
        
        setTimestamps({
          createdAt: docs.createdAt,
          updatedAt: docs.updatedAt
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        showNotification('Could not load existing documents', NOTIFICATION_TYPES.WARNING);
      }
    } finally {
      setLoading(false);
    }
  };

  const areAllDocumentsVerified = () => {
    const statuses = Object.values(documentStatuses);
    return statuses.every(status => status?.toUpperCase() === 'VERIFIED');
  };

  const hasRejectedDocuments = () => {
    return Object.values(documentStatuses).some(status => status?.toUpperCase() === 'REJECTED');
  };

  const getRejectedDocuments = () => {
    const rejected = [];
    if (documentStatuses.adhaarFront?.toUpperCase() === 'REJECTED') rejected.push('Aadhaar Card (Front)');
    if (documentStatuses.adhaarBack?.toUpperCase() === 'REJECTED') rejected.push('Aadhaar Card (Back)');
    if (documentStatuses.drivingLicense?.toUpperCase() === 'REJECTED') rejected.push('Driving License');
    return rejected;
  };

  const compressImageFile = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.8,
      fileType: 'image/jpeg'
    };
    try {
      console.log(`📷 Original size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
      const compressedFile = await imageCompression(file, options);
      console.log(`✅ Compressed to: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
      
      return new File([compressedFile], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } catch (error) {
      console.error('Compression error:', error);
      throw new Error('Failed to compress image');
    }
  };

  const handleFileSelect = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setErrors(prev => ({ ...prev, [documentType]: 'Only JPG, PNG, WEBP, or HEIC files are allowed' }));
      showNotification('Please upload an image file (JPG, PNG, WEBP, HEIC)', NOTIFICATION_TYPES.ERROR);
      return;
    }

    setErrors(prev => ({ ...prev, [documentType]: null }));
    setCompressing(prev => ({ ...prev, [documentType]: true }));

    try {
      const compressedFile = await compressImageFile(file);
      
      setNewDocuments(prev => ({ ...prev, [documentType]: compressedFile }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPreviews(prev => ({ ...prev, [documentType]: reader.result }));
        setCompressing(prev => ({ ...prev, [documentType]: false }));
        showNotification(`${documentType} selected and compressed!`, NOTIFICATION_TYPES.SUCCESS);
      };
      reader.onerror = () => {
        setErrors(prev => ({ ...prev, [documentType]: 'Failed to create preview' }));
        setCompressing(prev => ({ ...prev, [documentType]: false }));
      };
      reader.readAsDataURL(compressedFile);
    } catch (compressionError) {
      console.error('Compression error:', compressionError);
      setErrors(prev => ({ ...prev, [documentType]: 'Compression failed. Please try again.' }));
      setCompressing(prev => ({ ...prev, [documentType]: false }));
      showNotification('Failed to compress image. Please try again.', NOTIFICATION_TYPES.ERROR);
    }
  };

  const handleRemoveDocument = (documentType) => {
    setNewDocuments(prev => ({ ...prev, [documentType]: null }));
    setNewPreviews(prev => ({ ...prev, [documentType]: null }));
    
    if (fileInputRefs[documentType].current) {
      fileInputRefs[documentType].current.value = '';
    }
    
    showNotification(`${documentType} removed`, NOTIFICATION_TYPES.INFO);
  };

  const handleUploadAllDocuments = async () => {
    try {
      setUploading(true);
      const userId = getUserId();

      if (!userId) {
        showNotification('User ID not found. Please login again.', NOTIFICATION_TYPES.ERROR);
        return;
      }

      const hasNewDocuments = newDocuments.adhaarFront || newDocuments.adhaarBack || newDocuments.drivingLicense;
      if (!hasNewDocuments) {
        showNotification('Please select at least one document to upload', NOTIFICATION_TYPES.WARNING);
        return;
      }

      const formData = new FormData();
      formData.append('userId', userId.toString());

      if (newDocuments.adhaarFront) {
        formData.append('adhaarFront', newDocuments.adhaarFront);
      }
      if (newDocuments.adhaarBack) {
        formData.append('adhaarBack', newDocuments.adhaarBack);
      }
      if (newDocuments.drivingLicense) {
        formData.append('drivingLicense', newDocuments.drivingLicense);
      }

      const response = await api.post('/api/documents/upload-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.success || response.data) {
        showNotification('Documents uploaded successfully! Waiting for admin verification.', NOTIFICATION_TYPES.SUCCESS);
        
        setNewDocuments({ adhaarFront: null, adhaarBack: null, drivingLicense: null });
        setNewPreviews({ adhaarFront: null, adhaarBack: null, drivingLicense: null });
        
        Object.values(fileInputRefs).forEach(ref => {
          if (ref.current) ref.current.value = '';
        });
        
        setTimeout(() => {
          loadExistingDocuments();
        }, 1000);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload documents';
      showNotification(errorMessage, NOTIFICATION_TYPES.ERROR);
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case 'VERIFIED':
        return (
          <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-green-100 text-green-700">
            <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="hidden xs:inline">Verified</span>
            <span className="xs:hidden">✓</span>
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-red-100 text-red-700">
            <XCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="hidden sm:inline">Rejected - Re-upload</span>
            <span className="sm:hidden">Rejected</span>
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-yellow-100 text-yellow-700">
            <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="hidden xs:inline">Pending</span>
            <span className="xs:hidden">⏳</span>
          </span>
        );
    }
  };

  const handleImageError = (documentType) => {
    setImageErrors(prev => ({ ...prev, [documentType]: true }));
  };

  const handleImageLoad = (documentType) => {
    setImageErrors(prev => ({ ...prev, [documentType]: false }));
  };

  const renderDocumentCard = (documentType, title) => {
    const existingPreview = existingPreviews[documentType];
    const newPreview = newPreviews[documentType];
    const hasNewFile = !!newDocuments[documentType];
    const hasError = !!errors[documentType];
    const status = documentStatuses[documentType];
    const isRejected = status?.toUpperCase() === 'REJECTED';
    const isVerified = status?.toUpperCase() === 'VERIFIED';
    const imageError = imageErrors[documentType];
    const isCompressing = compressing[documentType];
    
    const displayPreview = newPreview || existingPreview;
    const hasPreview = !!displayPreview && !imageError;
    const showingNewPreview = !!newPreview;

    return (
      <div className={`bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 transition-all ${
        hasError ? 'border-red-300' : 
        isVerified ? 'border-green-300' :
        isRejected ? 'border-red-300 animate-pulse' :
        showingNewPreview ? 'border-blue-400' :
        'border-gray-200'
      }`}>
        {/* Card Header - Mobile Optimized */}
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2 flex-1 min-w-0">
              <PhotoIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" />
              <span className="truncate">{title}</span>
            </h3>
            {getStatusBadge(status)}
          </div>
        </div>

        {/* Card Body - Mobile Optimized */}
        <div className="p-3 sm:p-4 md:p-6">
          {/* Preview Area - Responsive Height */}
          <div className={`relative bg-gray-50 rounded-lg sm:rounded-xl overflow-hidden mb-3 sm:mb-4 border-2 border-dashed ${
            showingNewPreview ? 'border-blue-400 bg-blue-50' :
            hasPreview ? 'border-green-300' : 
            'border-gray-300'
          }`} style={{ minHeight: '200px' }}>
            {isCompressing ? (
              <div className="flex flex-col items-center justify-center h-full py-8 sm:py-12">
                <ArrowPathIcon className="h-12 w-12 sm:h-16 sm:w-16 text-blue-500 mb-3 sm:mb-4 animate-spin" />
                <p className="text-blue-600 font-semibold text-sm sm:text-base">Compressing...</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Please wait</p>
              </div>
            ) : hasPreview ? (
              <>
                <img 
                  src={displayPreview}
                  alt={title}
                  className="w-full h-full object-contain"
                  style={{ maxHeight: '240px' }}
                  crossOrigin="anonymous"
                  onError={() => handleImageError(documentType)}
                  onLoad={() => handleImageLoad(documentType)}
                />
                
                {/* New Upload Badge - Mobile Optimized */}
                {showingNewPreview && (
                  <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-blue-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-md sm:rounded-lg shadow-lg font-semibold text-[10px] sm:text-xs animate-bounce">
                    🆕 <span className="hidden xs:inline">New Upload</span>
                  </div>
                )}
                
                {/* Action Buttons - Mobile Touch-Friendly */}
                <div className="absolute top-1 sm:top-2 right-1 sm:right-2 flex gap-1 sm:gap-2">
                  <button
                    onClick={() => window.open(displayPreview, '_blank')}
                    className="p-1.5 sm:p-2 bg-blue-600 text-white rounded-md sm:rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all shadow-lg"
                    title="View Full Size"
                  >
                    <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                  {hasNewFile && (
                    <button
                      onClick={() => handleRemoveDocument(documentType)}
                      className="p-1.5 sm:p-2 bg-red-600 text-white rounded-md sm:rounded-lg hover:bg-red-700 active:bg-red-800 transition-all shadow-lg"
                      title="Remove Selection"
                    >
                      <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-8 sm:py-12">
                <DocumentIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mb-3 sm:mb-4" />
                <p className="text-gray-500 text-xs sm:text-sm text-center px-2">
                  {imageError ? 'Failed to load image' : 'No document uploaded'}
                </p>
                <p className="text-gray-400 text-[10px] sm:text-xs mt-1 sm:mt-2 text-center px-2">
                  Click button below
                </p>
              </div>
            )}
          </div>

          {/* Error Message - Mobile Optimized */}
          {hasError && (
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-600 flex items-start">
                <ExclamationTriangleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <span>{errors[documentType]}</span>
              </p>
            </div>
          )}

          {/* Rejection Message - Mobile Optimized */}
          {isRejected && !hasNewFile && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border-2 border-red-300 rounded-lg animate-pulse">
              <p className="text-xs sm:text-sm text-red-700 font-bold flex items-start mb-1 sm:mb-2">
                <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>⚠️ Document Rejected</span>
              </p>
              <p className="text-[10px] sm:text-xs text-red-600 ml-6 sm:ml-7">
                Please upload a new, clear image
              </p>
            </div>
          )}

          {/* New File Ready Message - Mobile Optimized */}
          {hasNewFile && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-700 font-bold flex items-start">
                <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0 mt-0.5 text-blue-600" />
                <span>✓ Ready to upload</span>
              </p>
              <p className="text-[10px] sm:text-xs text-blue-600 mt-1 ml-6 sm:ml-7">
                Click "Upload" button below
              </p>
            </div>
          )}

          {/* Action Button - Mobile Touch-Friendly */}
          <input
            ref={fileInputRefs[documentType]}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleFileSelect(e, documentType)}
            className="hidden"
          />
          
          {!isVerified && (
            <button
              onClick={() => fileInputRefs[documentType].current?.click()}
              disabled={uploading || isCompressing}
              className={`w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base active:scale-95 ${
                uploading || isCompressing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isRejected
                  ? 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-lg hover:shadow-xl'
                  : hasNewFile
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-lg'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 shadow-lg'
              }`}
            >
              {isCompressing ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  <span className="hidden xs:inline">Compressing...</span>
                  <span className="xs:hidden">⏳</span>
                </>
              ) : hasNewFile ? (
                <>
                  <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden xs:inline">✓ Ready to Upload</span>
                  <span className="xs:hidden">✓ Ready</span>
                </>
              ) : isRejected ? (
                <>
                  <CloudArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Re-Upload Document</span>
                  <span className="sm:hidden">Re-Upload</span>
                </>
              ) : existingPreview ? (
                <>
                  <PhotoIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Change Document</span>
                  <span className="sm:hidden">Change</span>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden xs:inline">Select Document</span>
                  <span className="xs:hidden">Select</span>
                </>
              )}
            </button>
          )}

          {/* Verified Lock - Mobile Optimized */}
          {isVerified && (
            <div className="w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold bg-green-50 text-green-700 text-center border-2 border-green-200 flex items-center justify-center gap-2 text-sm sm:text-base">
              <LockClosedIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden xs:inline">Document Verified ✓</span>
              <span className="xs:hidden">Verified ✓</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
          <p className="text-gray-600 font-semibold text-sm sm:text-base">Loading documents...</p>
        </div>
      </div>
    );
  }

  const hasAnyNewDocumentSelected = newDocuments.adhaarFront || newDocuments.adhaarBack || newDocuments.drivingLicense;
  const isAnyCompressing = Object.values(compressing).some(c => c);
  const newDocCount = [newDocuments.adhaarFront, newDocuments.adhaarBack, newDocuments.drivingLicense].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-4 sm:py-6 md:py-8 px-3 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header - Mobile Optimized */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center text-gray-600 hover:text-gray-900 active:text-gray-900 mb-3 sm:mb-4 transition-colors touch-manipulation"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            <span className="font-semibold text-sm sm:text-base">Back to Profile</span>
          </button>

          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-100">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              📄 Document Verification
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4">
              Upload your government-approved documents for verification. Images auto-compressed!
            </p>

            {/* All Verified Banner - Mobile Optimized */}
            {areAllDocumentsVerified() && (
              <div className="mt-3 sm:mt-4 p-4 sm:p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-xl">
                <div className="flex items-start gap-3 sm:gap-4">
                  <ShieldCheckIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-green-900 mb-1">All Documents Verified! ✓</h3>
                    <p className="text-xs sm:text-sm text-green-800">
                      You can now proceed with bike rentals.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Banner - Mobile Optimized */}
            {hasRejectedDocuments() && (
              <div className="mt-3 sm:mt-4 p-4 sm:p-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-500 rounded-xl">
                <div className="flex items-start gap-3 sm:gap-4">
                  <XCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-red-600 flex-shrink-0 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-red-900 mb-2">⚠️ Documents Rejected</h3>
                    <p className="text-xs sm:text-sm text-red-800 mb-2 font-semibold">
                      Re-upload required for:
                    </p>
                    <ul className="list-disc list-inside text-xs sm:text-sm text-red-800 space-y-0.5 sm:space-y-1 mb-2 sm:mb-3">
                      {getRejectedDocuments().map((doc, index) => (
                        <li key={index} className="font-medium">{doc}</li>
                      ))}
                    </ul>
                    <p className="text-[10px] sm:text-xs text-red-700 bg-red-100 p-2 rounded">
                      💡 Ensure clear, well-lit images
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Timestamps - Mobile Grid */}
            {(timestamps.createdAt || timestamps.updatedAt) && (
              <div className="mt-3 sm:mt-4 grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-4">
                {timestamps.createdAt && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs font-medium text-blue-600 mb-0.5">First Uploaded</p>
                      <p className="text-xs sm:text-sm font-semibold text-blue-900 truncate">{formatDateTime(timestamps.createdAt)}</p>
                      {/* <p className="text-[10px] sm:text-xs text-blue-700">{getTimeDifference(timestamps.createdAt)}</p> */}
                    </div>
                  </div>
                )}

                {timestamps.updatedAt && (
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs font-medium text-green-600 mb-0.5">Last Updated</p>
                      <p className="text-xs sm:text-sm font-semibold text-green-900 truncate">{formatDateTime(timestamps.updatedAt)}</p>
                      {/* <p className="text-[10px] sm:text-xs text-green-700">{getTimeDifference(timestamps.updatedAt)}</p> */}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Document Cards - Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {renderDocumentCard('adhaarFront', 'Aadhaar Card Front')}
          {renderDocumentCard('adhaarBack', 'Aadhaar Card Back')}
          {renderDocumentCard('drivingLicense', 'Driving License')}
        </div>

        {/* Upload Button - Mobile Sticky Bottom */}
        {hasAnyNewDocumentSelected && (
          <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 md:static md:mb-6">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 border-2 border-green-400 max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="text-white text-center sm:text-left">
                  <h3 className="text-base sm:text-lg font-bold mb-1 flex items-center justify-center sm:justify-start gap-2">
                    <CloudArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span>{newDocCount} Document{newDocCount > 1 ? 's' : ''} Ready</span>
                  </h3>
                  <p className="text-xs sm:text-sm text-green-100">
                    Click to submit for admin review
                  </p>
                </div>
                <button
                  onClick={handleUploadAllDocuments}
                  disabled={uploading || isAnyCompressing}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base active:scale-95 ${
                    uploading || isAnyCompressing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-white text-green-600 hover:bg-green-50 active:bg-green-100'
                  }`}
                >
                  {uploading ? (
                    <>
                      <CloudArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6 animate-bounce" />
                      <span className="hidden xs:inline">Uploading {newDocCount}...</span>
                      <span className="xs:hidden">Uploading...</span>
                    </>
                  ) : isAnyCompressing ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                      Compressing...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="hidden xs:inline">Upload {newDocCount} Document{newDocCount > 1 ? 's' : ''}</span>
                      <span className="xs:hidden">Upload {newDocCount}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add padding at bottom for mobile sticky button */}
        {hasAnyNewDocumentSelected && <div className="h-24 sm:h-0"></div>}

        {/* Guidelines - Mobile Optimized */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <DocumentIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-xs sm:text-sm font-bold text-blue-900 mb-2">📋 Upload Guidelines</h4>
              <ul className="text-[10px] sm:text-xs md:text-sm text-blue-800 space-y-1">
                <li>• ✅ <strong>No size limit!</strong> Auto-compressed to ~1MB</li>
                <li>• 📸 Take clear, well-lit photos</li>
                <li>• 📄 Formats: JPG, PNG, WEBP, HEIC</li>
                <li>• 🔄 Re-upload anytime</li>
                <li>• ⏱️ Admin review within 24-48 hours</li>
                <li>• 🔒 Verified docs cannot be changed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentVerification;
